import type {
    CoverableDTO,
    CoverageDTO,
    TermDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import { formatStringToMonetaryValueString, getCoverageFromCoverable } from './coverablesUtil';

function isTermForcedDeductible(term: TermDTO): boolean {
    const termType = term.publicId ?? '';
    return /Forced.*Deduct/g.test(termType) || /Forced/g.test(termType);
}

function isTermTypeOfDeductible(term: TermDTO): boolean {
    return /DeductibleType/g.test(term.publicId ?? '');
}

function isTermDeductible(term: TermDTO): boolean {
    const termType = term.publicId ?? '';
    return (
        !isTermForcedDeductible(term) &&
        (/Deduct/g.test(termType) || (/Cov.*Ded/g.test(termType) && Number(term.value) !== 0))
    );
}

function isTermMainDeductible(term: TermDTO, lobName: string): boolean {
    const termType = term.publicId ?? '';
    if (lobName === 'Indboforsikring' || lobName === 'Indboforsikring til unge') {
        const contentTermArray = [
            'OptionPRHLiabilityCovDeductibleLawyerType',
            'PRCLiabilityCovAnimalObject',
            'PRCLiabilityCovTermLimitPerson'
        ];
        return (
            !isTermForcedDeductible(term) &&
            !isTermTypeOfDeductible(term) &&
            !contentTermArray.includes(termType)
        );
    }
    const termArray = [
        'OptionLBVLegalAidCovPercentageDedType',
        'OptionLBVLegalAidCovMinimumDedType',
        'MVPWindscreenProtectionDeductible',
        'MVPLiabilityDeductible_Car20',
        'MVPComprehensiveDeductible_Car20',
        'MVPLegalAidDedPercWithLegalCounselling'
    ];

    return (
        !isTermForcedDeductible(term) &&
        (/CovDeductibles|CovDedPerc|CovDedMinimum/g.test(termType) || termArray.includes(termType))
    );
}

function isTermSecondaryDeductible(term: TermDTO, lobName: string): boolean {
    const termsArray = [
        // Private Content
        'OptionPRCContentAndBaggageCovCloudBurstType',
        'OptionPRCContentAndBaggageCovIdentityTheftType',
        'OptionPRCContentAndBaggageCovTraumaCounselingType',
        // Car
        'MVPLeasingcarReturnDamageExclVAT',
        'MVPLeasingcarReturnTotalCompExclVAT',
        'MVPLeasingcarReturnAllDamagesInclVAT'
    ];
    const termType = term.publicId ?? '';
    return (
        !isTermMainDeductible(term, lobName) &&
        !isTermTypeOfDeductible(term) &&
        (isTermDeductible(term) || termsArray.includes(termType))
    );
}

const getDeductiblesText = (
    coverage: CoverageDTO,
    mainDeductibles: TermDTO[],
    secondaryDeductibles: TermDTO[]
): string => {
    const excludeDeductibleTextCoverages = ['MVPYoungDriver'];

    switch (true) {
        case excludeDeductibleTextCoverages.includes(coverage.type ?? ''):
            return ' ';
        case mainDeductibles.length === 0 && secondaryDeductibles.length === 0:
            return 'Ingen selvrisiko';
        case mainDeductibles.length > 1 || secondaryDeductibles.length > 1:
            return 'Varierende selvrisiko';
        case mainDeductibles.length === 1:
            return `${'Selvrisiko: '}${formatStringToMonetaryValueString(
                mainDeductibles[0].value ?? ''
            )}`;
        default:
            return 'Ingen selvrisiko';
    }
};

function processBodyTerms(terms: TermDTO[], lobName: string): ProcessedTerms {
    const mainDeductibles = terms.filter((term: TermDTO) => isTermMainDeductible(term, lobName));
    const secondaryDeductibles = terms.filter((term: TermDTO) =>
        isTermSecondaryDeductible(term, lobName)
    );

    return {
        mainDeductibles,
        secondaryDeductibles
    };
}
interface ProcessedTerms {
    mainDeductibles: TermDTO[];
    secondaryDeductibles: TermDTO[];
}

const getTermFromCoverage = (
    coverage: CoverageDTO | undefined,
    termId: string
): TermDTO | undefined => {
    return coverage?.terms?.find((term: TermDTO) => term.publicId === termId);
};

const getTermValue = (coverable: CoverableDTO, coverageType: string, termId: string): string => {
    return (
        getTermFromCoverage(getCoverageFromCoverable(coverable.coverages, coverageType), termId)
            ?.value ?? ''
    );
};

const getTermMonetaryValue = (
    coverable: CoverableDTO,
    coverageType: string,
    termId: string
): string => {
    return formatStringToMonetaryValueString(getTermValue(coverable, coverageType, termId));
};

export {
    getDeductiblesText,
    processBodyTerms,
    isTermMainDeductible,
    isTermSecondaryDeductible,
    getTermFromCoverage,
    getTermValue,
    getTermMonetaryValue
};

export type { ProcessedTerms };
